import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Image } from '../../Image';
import { trackBoxToggle } from '@/lib/reportBoxToggle';
import { toSpecialNameSlug } from '@/lib/reportAnalyticsSections';
import { getFilledCreators } from '../utils/hasContent';
import { useReportI18n } from '@/pages/reports/i18n';

export interface CreatorEntry {
  imageUrl: string;
  name: string;
  username: string;
  views: string;
  likes: string;
  link?: string;
}

export interface CollapsibleCreatorsProps {
  title?: string;
  subtitle?: string;
  creators?: CreatorEntry[];
}

export const CollapsibleCreators: React.FC<CollapsibleCreatorsProps> = ({
  title,
  subtitle,
  creators = [],
}) => {
  const { t } = useReportI18n();
  const [isOpen, setIsOpen] = useState(true);
  const heading = title || t('tiktokTrends.creators');
  const description = subtitle || t('tiktokTrends.creatorsSubtitle');
  const boxSectionName = toSpecialNameSlug(heading);
  const filledCreators = getFilledCreators(creators);

  if (filledCreators.length === 0) {
    return null;
  }

  return (
    <div className="w-full flex justify-center">
      <div
        className="w-full bg-white overflow-hidden"
        data-report-box
        data-report-box-section-name={boxSectionName}
      >
        <button
          type="button"
          onClick={(event) => {
            const next = !isOpen;
            setIsOpen(next);
            trackBoxToggle(next, event.currentTarget.parentElement);
          }}
          className="w-full flex items-center gap-2 bg-black text-white px-4 py-3 text-left hover:bg-gray-900 transition-colors font-['Times_New_Roman']"
        >
          {isOpen ? (
            <ChevronUp className="w-5 h-5 shrink-0" />
          ) : (
            <ChevronDown className="w-5 h-5 shrink-0" />
          )}
          <span className="text-[36px] leading-[40px] tracking-tight">{heading}</span>
        </button>
        {isOpen && (
          <div className="p-4 pt-2 space-y-6 font-['Times_New_Roman']">
            {description && (
              <p className="text-sm text-gray-600">{description}</p>
            )}
            <div className="space-y-6">
              {filledCreators.map((creator, idx) => {
                  const content = (
                    <>
                      <div className="shrink-0 w-20 h-20 rounded-full overflow-hidden border border-gray-200">
                        <Image
                          src={creator.imageUrl}
                          alt={creator.name}
                          className="w-full h-full object-cover rounded-full"
                        />
                      </div>
                      <div className="min-w-0 flex-1 space-y-0.5">
                        <p className="font-['Times_New_Roman'] font-bold text-gray-900 text-xl leading-none underline">
                          {creator.name}
                        </p>
                        <p className="font-['Times_New_Roman'] font-normal text-gray-700 text-base">
                          @{creator.username.replace(/^@/, '')}
                        </p>
                        <p className="font-['Times_New_Roman'] italic text-gray-600 text-sm mt-1">
                          {creator.views}
                        </p>
                        {creator.likes && (
                          <p className="font-['Times_New_Roman'] italic text-gray-600 text-sm">
                            {creator.likes}
                          </p>
                        )}
                      </div>
                    </>
                  );
                  return (
                    <div key={idx} className="flex gap-4 items-start">
                      {creator.link ? (
                        <a
                          href={creator.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex gap-4 items-start hover:opacity-90"
                        >
                          {content}
                        </a>
                      ) : (
                        content
                      )}
                    </div>
                  );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
